import React, { useCallback, useContext, useMemo } from 'react';
import { orange } from '@mui/material/colors';
import { DataGridPremium, GridCellEditStopReasons } from '@mui/x-data-grid-premium';
import { useGetRowHeight } from 'common/hooks/useGetRowHeight';
import { uniq } from 'lodash/array';
import { omit } from 'lodash/object';
import { muiDataGridLocaleText } from 'services/i18n/mui';

import { usePMWorkbookColumns } from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/usePMWorkbookColumns';

import { PMWorkbookContext } from '../PMWorkbookContext';

import { ColumnsPanelWithGrouping } from './TableComponents/ColumnsPanelWithGrouping/ColumnsPanelWithGrouping';
import { PMWorkbookToolbar } from './TableComponents/PMWorkbookToolbar';

export const PMWorkbookTable = () => {
  const { tableData, processRowUpdate, apiRef, refreshData } = useContext(PMWorkbookContext);

  const { columns, tableVersion, tableName } = usePMWorkbookColumns();

  const columnGroupingModel = useMemo(() => {
    if (!columns || columns.length === 0) {
      return [];
    }

    const groups = uniq(columns.filter(col => !!col.group).map(col => col.group));

    return groups.map(group => ({
      groupId: group,
      description: '',
      children: columns.filter(col => col.group === group).flatMap(c => ({ field: c.field }))
    }));
  }, [columns]);

  const defaultHiddenColumns = useMemo(() => {
    let res = {};
    columns.forEach(col => {
      res[col.field] = false; // 🔒 Hidden by default
    });
    return res;
  }, [columns]);

  const columnVisibilityModel = useMemo(() => {
    return {
      ...defaultHiddenColumns,
      ...{
        siteName: true, // ✅ Visible by default
        subjectId: true,
        patientId: true,
        paymentCard: true,
        edcVisitDate: true,
        patientSource: true,
        patientStatus: true,
        edcInitiatedDate: true,
        edcCompletedBy: true,
        edcCompletionDate: true,
        bloodDrawStipend: true,
        eproStipend: true,
        dateOfScheduledColonoscopy: true,
        actualDateOfColonoscopy: true,
        coloReportReceived: true,
        pathologyReported: true,
        pathReportReceived: true,
        colonoscopyEdcCompletionDate: true,
        colonoscopyStipend: true,
        eosCompletionDate: true,
        eosReviewer: true,
        eosReason: true,
        recordsAssistanceStipend: true,
        currentSubjectStatus: true,
        tasks: true,
        comments: true
      }
    };
  }, [defaultHiddenColumns]);

  const defaultTableState = useMemo(() => {
    return {
      columns: {
        columnVisibilityModel: columnVisibilityModel,
        orderedFields: columns.map(col => col.field)
      },
      filter: {
        filterModel: {
          items: [
            {
              field: 'patientStatus',
              operator: 'isAnyOf',
              value: ['Consented', 'Screened', 'Randomized']
            }
          ],
          quickFilterExcludeHiddenColumns: false
        }
      }
    };
  }, [columnVisibilityModel, columns]);

  const dateFieldFormats = useMemo(
    () =>
      Object.fromEntries(
        (columns || []).filter(col => col.type === 'date').map(col => [col.field, { numFmt: 'dd/MMM/yyyy' }])
      ),
    [columns]
  );

  const toolbar = useCallback(() => {
    return (
      <PMWorkbookToolbar
        defaultTableState={defaultTableState}
        dateFieldFormats={dateFieldFormats}
        columnGroupingModel={columnGroupingModel}
        columns={columns}
        tableVersion={tableVersion}
        tableName={tableName}
        refreshData={refreshData}
      />
    );
  }, [defaultTableState, dateFieldFormats, columnGroupingModel, columns, tableVersion, tableName, refreshData]);

  const columnsPanel = useCallback(() => {
    return <ColumnsPanelWithGrouping defaultTableState={defaultTableState} />;
  }, [defaultTableState]);

  // Remove group from columns
  const finalColumns = useMemo(() => {
    return columns.map(col => omit(col, ['group']));
  }, [columns]);

  const getRowHeight = useGetRowHeight();

  return (
    <DataGridPremium
      apiRef={apiRef}
      processRowUpdate={processRowUpdate}
      initialState={{
        columns: {
          // Only for initial page render with table UI but without any columns
          // Columns will be updated after CustomViews loaded
          // If CustomViews are empty - default table state will be used
          columnVisibilityModel: defaultHiddenColumns,
          filter: {
            filterModel: {
              items: [],
              quickFilterExcludeHiddenColumns: false
            }
          }
        }
      }}
      getRowId={row => row.ssuPatientId}
      rows={tableData}
      columnGroupingModel={columnGroupingModel}
      columns={finalColumns}
      disableRowSelectionOnClick
      localeText={muiDataGridLocaleText({
        noRowsLabel: 'No results to display',
        noResultsOverlayLabel: 'No results were found. Try modifying your criteria.'
      })}
      getRowHeight={getRowHeight}
      disableRowGrouping
      disableAggregation
      disablePivoting
      sx={{
        padding: '0 40px 0 40px',
        border: 'none',
        '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '9px' },
        '.duplicated-patient-id': {
          backgroundColor: orange[100]
        }
      }}
      slots={{
        columnsPanel: columnsPanel,
        toolbar: toolbar
      }}
      slotProps={{
        root: { 'data-testid': 'pm-workbook-table' },
        toolbar: { 'data-testid': 'pm-workbook-table-toolbar' }
      }}
      onCellEditStop={(params, event) => {
        if (params.reason !== GridCellEditStopReasons.enterKeyDown) {
          return;
        }
        if (event.altKey || event.metaKey) {
          event.defaultMuiPrevented = true;
        }
      }}
      getCellClassName={({ field, value, row }) => {
        if (field === 'patientId' && value !== null) {
          return row.patientIdDuplicated ? 'duplicated-patient-id' : '';
        }
        return '';
      }}
      showToolbar
    />
  );
};
